// src/pages/misc/landing/LandingMenu.tsx
// [HANDOFF] Slide-in side menu shared by the landing (LandingPage.tsx) and every public page (public/PublicShell.tsx).
//   Props: open / view ('main' | 'contacto') / lang ('es' | 'en') and callbacks. The parent owns the state.
//   Entries, in order: "Empezar ahora" (gold button) -> /onboarding, then Cómo funciona, Socios / Programa de referidos, Preguntas frecuentes,
//   Contacto, Iniciar sesión (-> /login, NOT part of the swap), and the Español / English switch at the bottom.
// [URL-RENAME] Navigates with typed Spanish paths. When English URLs arrive change: /como-funciona -> /how-it-works, /socios -> /partners,
//   /preguntas -> /faq, /contacto -> /contact (that one already exists as an older page, decide which wins). /onboarding and /login stay.
// [I18N] The English / Español switch only changes the `lang` state the parent passes; the page texts themselves are still Spanish.
// [VERIFY] The "contacto" view (opened by the parent with view='contacto') has a WhatsApp button pointing at the bare https://wa.me/ (no phone
//   number) and a note form that opens the user's mail app (mailto) to a hardcoded address. Nothing is stored on a server. Check both before launch.
// [SPACING] Panel / scrim / link sizes live in landing.css (.vl-panel, .vl-scrim, .vl-menu-link, .vl-menu-cta).
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Rocket } from 'lucide-react';
import { VENTAJE_LOGIN_URL } from '../../../config';
import { WA_PHONE } from '../public/wa';

// WU-141 S-10 (i): the language switch is hidden until English exists - the page texts are Spanish whatever `lang` is, so it changes nothing today.
const SHOW_LANGUAGE_SWITCH = false;

export type MenuView = 'main' | 'contacto';

interface LandingMenuProps {
  open: boolean;
  view: MenuView;
  lang: 'es' | 'en';
  onClose: () => void;
  onView: (view: MenuView) => void;
  onLang: (lang: 'es' | 'en') => void;
  onGo: (selector: string) => void;
}

const LandingMenu: React.FC<LandingMenuProps> = ({
  open,
  view,
  lang,
  onClose,
  onView,
  onLang,
}) => {
  const navigate = useNavigate();
  const [note, setNote] = useState({ name: '', email: '', msg: '' });
  const [sent, setSent] = useState(false);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const sendNote = () => {
    if (!note.email.trim() || !note.msg.trim()) return;
    const subject = encodeURIComponent('Mensaje desde Ventaje');
    const body = encodeURIComponent(`${note.name}\n${note.email}\n\n${note.msg}`);
    window.location.href = `mailto:Merivilla@merivilla.com?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <>
      <div
        className={`vl-scrim${open ? ' on' : ''}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside className={`vl-panel${open ? ' on' : ''}`} aria-hidden={!open}>
        <div className="vl-panel-head">
          <span className="vl-logo">Ventaje</span>
          <button type="button" className="vl-close-x" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {view === 'main' && (
          <div>
            <button
              type="button"
              className="btn-gold vl-menu-cta inline-flex items-center justify-center gap-2 min-h-[52px] rounded-[10px] text-[12px] font-bold uppercase tracking-[0.1em]"
              onClick={() => go('/onboarding')}
            >
              <Rocket size={16} /> Empezar ahora
            </button>
            <button type="button" className="vl-menu-link" onClick={() => go('/como-funciona')}>
              Cómo funciona
            </button>
            <button type="button" className="vl-menu-link" onClick={() => go('/socios')}>
              Socios / Programa de referidos
            </button>
            <button type="button" className="vl-menu-link" onClick={() => go('/preguntas')}>
              Preguntas frecuentes
            </button>
            <button type="button" className="vl-menu-link" onClick={() => go('/contacto')}>
              Contacto
            </button>
            <button type="button" className="vl-menu-link" onClick={() => window.location.assign(VENTAJE_LOGIN_URL)}>
              <span className="inline-flex items-center gap-2"><LogIn size={16} /> Iniciar sesión</span>
            </button>
            {SHOW_LANGUAGE_SWITCH && (
              <div className="vl-panel-foot">
                <span className="vl-tag">Idioma</span>
                <div className="vl-lang">
                  <button
                    type="button"
                    className={lang === 'es' ? 'on' : ''}
                    onClick={() => onLang('es')}
                  >
                    Español
                  </button>
                  <button
                    type="button"
                    className={lang === 'en' ? 'on' : ''}
                    onClick={() => onLang('en')}
                  >
                    English
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'contacto' && (
          <div>
            <button type="button" className="vl-back-link" onClick={() => onView('main')}>
              ← Menú
            </button>
            <h2 className="vl-h2" style={{ fontSize: 'clamp(26px, 3.4vw, 38px)' }}>
              Escríbenos
            </h2>
            <p className="vl-lead">Por WhatsApp te contestamos el mismo día.</p>
            <div style={{ marginTop: 22 }}>
              <a
                href={`https://wa.me/${WA_PHONE}`}
                target="_blank"
                rel="noreferrer"
                className="btn-gold inline-flex items-center justify-center min-h-[54px] px-7 rounded-[10px] text-[13px] font-bold uppercase tracking-[0.1em]"
              >
                Abrir WhatsApp →
              </a>
            </div>
            <p className="vl-lead" style={{ marginTop: 28, fontSize: 15 }}>O déjanos un recado y te escribimos.</p>
            {sent ? (
              <p className="vl-lead" style={{ marginTop: 12 }}>Listo. Se abrió tu correo para enviarlo.</p>
            ) : (
              <div className="vl-note">
                <input placeholder="Tu nombre" value={note.name} onChange={(e) => setNote({ ...note, name: e.target.value })} />
                <input placeholder="Tu correo" type="email" value={note.email} onChange={(e) => setNote({ ...note, email: e.target.value })} />
                <textarea placeholder="¿En qué te ayudamos?" rows={3} value={note.msg} onChange={(e) => setNote({ ...note, msg: e.target.value })} />
                <button type="button" className="vl-note-send" onClick={sendNote}>Enviar</button>
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default LandingMenu;
